import { useMemo } from 'react';
import type { Currency, DailyAggregate, Period } from '@etiquetachef/types';
import { previousPeriod } from '@/lib/period';
import { useDailyAggregates, type DailyAggregatesState } from './useDailyAggregates';

export type FinanceMetrics = {
  // KPIs do período
  mrrUsdAvgCents: number;
  mrrByCurrencyAvgCents: Record<Currency, number>;
  arptUsdAvgCents: number;
  newTenants: number;
  newMembers: number;
  labelsPrinted: number | null;
  labelsHasPreDeployGap: boolean;
  netNewMrrUsdCents: number;
  churnedTenants: number;
  churnRatePct: number;
  // Deltas vs período anterior (null se não computável)
  deltas: {
    mrrUsd: number | null;
    arptUsd: number | null;
    newTenants: number | null;
    newMembers: number | null;
    labels: number | null;
    netNewMrrUsd: number | null;
    churnedTenants: number | null;
  };
  // Série bruta para charts/tabelas
  series: DailyAggregate[];
};

export type FinanceMetricsState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | { kind: 'READY'; metrics: FinanceMetrics };

/** Média aritmética; lista vazia → 0. */
function avg(values: readonly number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/** Soma simples. */
function sum(values: readonly number[]): number {
  return values.reduce((a, b) => a + b, 0);
}

/**
 * Variação fracionária vs período anterior; base 0 → `null` (UI mostra "—"). Com base
 * negativa (Net New MRR) o sinal sai invertido em relação à leitura intuitiva.
 */
function deltaPct(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return (current - previous) / previous;
}

/** KPIs de um período: MÉDIA para MRR/ARPT (retrato), SOMA para os fluxos (ver hook). */
function aggregate(
  series: readonly DailyAggregate[],
): Omit<FinanceMetrics, 'deltas' | 'series'> & { activeAtEnd: number } {
  if (series.length === 0) {
    return {
      mrrUsdAvgCents: 0,
      mrrByCurrencyAvgCents: { BRL: 0, USD: 0, EUR: 0 },
      arptUsdAvgCents: 0,
      newTenants: 0,
      newMembers: 0,
      labelsPrinted: null,
      labelsHasPreDeployGap: false,
      netNewMrrUsdCents: 0,
      churnedTenants: 0,
      churnRatePct: 0,
      activeAtEnd: 0,
    };
  }

  const mrrUsds = series.map((d) => d.mrr.usdEquivalentCents);
  const arpts = series.map((d) => d.mrr.arptUsdCents);
  const labelsValues = series.map((d) => d.labels.printedToday);
  const hasPreDeployGap = labelsValues.some((v) => v === null);
  const labelsKnown = labelsValues.filter((v): v is number => v !== null);

  const newTenants = sum(series.map((d) => d.tenants.createdToday));
  const churned = sum(series.map((d) => d.tenants.suspendedToday));
  const activeAtEnd = series[series.length - 1]?.tenants.active ?? 0;
  const churnRate = activeAtEnd === 0 ? 0 : churned / activeAtEnd;

  return {
    mrrUsdAvgCents: Math.round(avg(mrrUsds)),
    mrrByCurrencyAvgCents: {
      BRL: Math.round(avg(series.map((d) => d.mrr.byCurrency.BRL))),
      USD: Math.round(avg(series.map((d) => d.mrr.byCurrency.USD))),
      EUR: Math.round(avg(series.map((d) => d.mrr.byCurrency.EUR))),
    },
    arptUsdAvgCents: Math.round(avg(arpts)),
    newTenants,
    newMembers: sum(series.map((d) => d.members.createdToday)),
    labelsPrinted: labelsKnown.length === 0 ? null : sum(labelsKnown),
    labelsHasPreDeployGap: hasPreDeployGap,
    netNewMrrUsdCents: sum(series.map((d) => d.netNewMrr.netUsdCents)),
    churnedTenants: churned,
    churnRatePct: churnRate,
    activeAtEnd,
  };
}

/**
 * Calcula os KPIs financeiros do período e os deltas vs o período anterior de mesma duração.
 *
 * Origem: sem issue — commit 498e432c6, 2026-05-04 15:52 +0200 (rota /dashboard/finance).
 *
 * Por quê: duas leituras de `useDailyAggregates` (atual e `previousPeriod`), combinadas aqui
 * para a UI ter um estado só. MRR e ARPT são MÉDIA dos dias (retrato); tenants, membros,
 * etiquetas e Net New MRR são SOMA (fluxo); churn = suspensos no período ÷ ativos no último
 * dia. Trocar média por soma no MRR multiplicaria o KPI pelo nº de dias.
 *
 * Contrato: `ERROR` se qualquer uma das duas leituras falhar; `LOADING` enquanto uma delas
 * carrega. Período sem dados → tudo zero, deltas `null`; período anterior zerado → delta
 * `null` (UI mostra "—"). Etiquetas: dia com `printedToday === null` (pré-deploy da métrica)
 * é excluído da soma e marca `labelsHasPreDeployGap` para o tooltip; delta de etiquetas só
 * quando os dois períodos têm valor. `churnRatePct` é fração (0–1), não percentual.
 *
 * Usado por: `FinanceDashboardPage`, `KpiCards`, `RevenueByCurrencyTable` (`apps/admin`).
 *
 * Falhas conhecidas:
 *  - 2026-05-08 (B-25, commit 2a4a9a93e): `previousPeriod(period)` devolvia objeto novo a
 *    cada render e desestabilizava consumidores que recebiam `period` por referência —
 *    memoizado por `(from, to)`; ver comentário inline.
 *
 * Dados: série de `DailyAggregate` (centavos; `packages/types/src/aggregations.ts`).
 */
export function useFinanceMetrics(period: Period): FinanceMetricsState {
  // B-25: `previousPeriod(period)` retorna `{ from, to }` recém-criado a
  // cada render. Sem memoização aqui, `asPeriod(...)` virava um objeto
  // novo em todo render — `useDailyAggregates` ignora isso porque
  // depende de `[period.from, period.to]` no effect, mas qualquer
  // consumidor downstream que pegue a prop `period` por referência
  // (ex.: PeriodPicker em testes) sofre com instabilidade. Memoizar
  // por `(period.from, period.to)` é barato e mais previsível.
  const previousPeriodValue = useMemo(
    () => asPeriod(previousPeriod(period)),
    // Memoizar por (from, to) é correto: `previousPeriod` só lê esses
    // dois campos; outros campos do `period` (ex.: preset) são derivados.
    // ESLint exhaustive-deps não consegue inferir isso, daí o disable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [period.from, period.to],
  );
  const current = useDailyAggregates(period);
  const previous = useDailyAggregates(previousPeriodValue);

  return useMemo(
    () => combine(current, previous),
    [current, previous],
  );
}

/** Envolve um `{ from, to }` como `Period` CUSTOM para o `useDailyAggregates`. */
function asPeriod(range: { from: string; to: string }): Period {
  return { preset: 'CUSTOM', from: range.from, to: range.to };
}

/** Junta os dois estados: ERROR vence, depois LOADING; só calcula com os dois READY. */
function combine(
  current: DailyAggregatesState,
  previous: DailyAggregatesState,
): FinanceMetricsState {
  if (current.kind === 'ERROR') return { kind: 'ERROR', error: current.error };
  if (previous.kind === 'ERROR') return { kind: 'ERROR', error: previous.error };
  if (current.kind === 'LOADING' || previous.kind === 'LOADING') {
    return { kind: 'LOADING' };
  }

  const cur = aggregate(current.aggregates);
  const prev = aggregate(previous.aggregates);

  const labelsCurForDelta = cur.labelsPrinted;
  const labelsPrevForDelta = prev.labelsPrinted;

  const metrics: FinanceMetrics = {
    mrrUsdAvgCents: cur.mrrUsdAvgCents,
    mrrByCurrencyAvgCents: cur.mrrByCurrencyAvgCents,
    arptUsdAvgCents: cur.arptUsdAvgCents,
    newTenants: cur.newTenants,
    newMembers: cur.newMembers,
    labelsPrinted: cur.labelsPrinted,
    labelsHasPreDeployGap: cur.labelsHasPreDeployGap,
    netNewMrrUsdCents: cur.netNewMrrUsdCents,
    churnedTenants: cur.churnedTenants,
    churnRatePct: cur.churnRatePct,
    deltas: {
      mrrUsd: deltaPct(cur.mrrUsdAvgCents, prev.mrrUsdAvgCents),
      arptUsd: deltaPct(cur.arptUsdAvgCents, prev.arptUsdAvgCents),
      newTenants: deltaPct(cur.newTenants, prev.newTenants),
      newMembers: deltaPct(cur.newMembers, prev.newMembers),
      labels:
        labelsCurForDelta != null && labelsPrevForDelta != null
          ? deltaPct(labelsCurForDelta, labelsPrevForDelta)
          : null,
      netNewMrrUsd: deltaPct(cur.netNewMrrUsdCents, prev.netNewMrrUsdCents),
      churnedTenants: deltaPct(cur.churnedTenants, prev.churnedTenants),
    },
    series: current.aggregates,
  };

  return { kind: 'READY', metrics };
}
